/**
 * Brand constants — single source of truth for Pixelmojo identity used by
 * structured-data.tsx (global graph) and AutoSchema (per-page baseline).
 *
 * Anything brand-facing (logo URL, contact, address, sameAs, knowsAbout
 * topic list) lives here, not inlined in schema generators. Update once,
 * every JSON-LD emitter picks it up.
 */

export const ORG_ID = 'https://www.pixelmojo.io/#organization'
export const WEBSITE_ID = 'https://www.pixelmojo.io/#website'
export const BASE_URL = 'https://www.pixelmojo.io'

/** Reciprocal language cluster for the English and Arabic Radar landings. */
export const RADAR_EN_URL = `${BASE_URL}/platform`
export const RADAR_AR_URL = `${BASE_URL}/ar/radar`
export const RADAR_LANGUAGE_ALTERNATES = {
  en: RADAR_EN_URL,
  ar: RADAR_AR_URL,
  'ar-SA': RADAR_AR_URL,
  'ar-AE': RADAR_AR_URL,
  'x-default': RADAR_EN_URL,
} as const

/** Canonical author/Person node. The full Person schema lives on the author
 *  page (/author/lloyd-pilapil); other emitters reference it by @id + url
 *  rather than redefining it. Single source of truth for author identity. */
export const AUTHOR_PROFILE_URL = `${BASE_URL}/author/lloyd-pilapil`
export const AUTHOR_ID = `${AUTHOR_PROFILE_URL}#person`
export const AUTHOR_NAME = 'Lloyd Pilapil'
/** Founder headshot, a photo (800×800). The ONE image for the Person node,
 *  the /author page and its OG image, the author data and the /about founder
 *  card. Same likeness as the founder introduction on YouTube, so the site,
 *  the channel and LinkedIn show one recognisable person. */
export const AUTHOR_HEADSHOT_PATH = '/lloyd-pilapil-portrait.webp'
export const AUTHOR_HEADSHOT_URL = `${BASE_URL}${AUTHOR_HEADSHOT_PATH}`
export const AUTHOR_HEADSHOT_SIZE = 800
/** Canonical job title. Emitted by the author page's full Person node and by
 *  the reference Person node in the site-wide graph, so the two can never
 *  disagree about who Lloyd is. */
export const AUTHOR_JOB_TITLE = 'Founder & AI Product Architect'

/** Date of the last substantive homepage content change (ISO 8601, UTC).
 *  Single source of truth for BOTH the homepage WebPage.dateModified node
 *  (src/app/structured-data.tsx) and the `/` Last-Modified response header
 *  (next.config.ts). Deliberately a fixed editorial date, not build time:
 *  a deploy is not a content change, and AEO auditors read a build-stamped
 *  Last-Modified as freshness we did not earn.
 *
 *  Update this when homepage copy or sections materially change. This file
 *  has zero imports on purpose so next.config.ts can read it safely. */
export const HOMEPAGE_DATE_MODIFIED = '2026-10-10'

/** Last meaningful update to the AI-facing manifest/knowledge-graph surface.
 * Included in both llms routes so project and entity changes advance their
 * editorial freshness even when no blog post or homepage section changes. */
export const AI_DISCOVERABILITY_DATE_MODIFIED = '2026-10-10'

/** Last substantive change to /platform/methodology copy (PIX-173, the ChatGPT
 * model scoring notice). Shared by
 * the sitemap and the page's WebPage dateModified; update it only when the
 * methodology itself changes, never on a deploy. */
export const METHODOLOGY_DATE_MODIFIED = '2026-10-16'

/** RFC 7231 form of HOMEPAGE_DATE_MODIFIED, for the Last-Modified header. */
export const HOMEPAGE_LAST_MODIFIED_HTTP = new Date(
  `${HOMEPAGE_DATE_MODIFIED}T00:00:00Z`
).toUTCString()

/** Timezone offset for Pixelmojo (Makati / Asia/Manila, no DST). Used to
 *  emit timezone-aware ISO-8601 datetimes that pass Google Rich Results. */
export const SITE_TZ_OFFSET = '+08:00'

export const BRAND = {
  name: 'Pixelmojo',
  legalName: 'Pixelmojo AI Product Studio',
  alternateName: ['Pixelmojo AI', 'pixelmojo.io'],
  url: BASE_URL,
  logo: `${BASE_URL}/pixelmojo-branding-new.svg`,
  image: `${BASE_URL}/pixelmojo-og-2026.png`,
  description:
    'AI product studio (not a marketing or design agency) founded in 2024 in Makati, Metro Manila, Philippines. Operates Radar, its self-service AI visibility audit platform, and delivers Vector (lead qualification) and Hive (multi-agent orchestration) as custom builds whose application code the client owns.',
  disambiguatingDescription:
    'Pixelmojo is a Philippines-based AI product studio headquartered in Makati, Metro Manila, founded in 2024 by Lloyd Pilapil. Pixelmojo is NOT a digital marketing or design agency, NOT a US-based or San Diego company, and NOT an AI image generator or AI art generator (it has no credit-based or per-image generation model). Not affiliated with: Pixel Mojo, the Minneapolis-based VR/AR visualization company founded in 2008; nor with Pixel Mojo Software, a separate company using a similar name; nor with Pixel Mojo Productions, a separate company using a similar name; nor with any pixel art editor application using a similar name (developed by independent authors, unrelated to this company). None of these share ownership, staff, products, or a website with Pixelmojo (pixelmojo.io). Pixelmojo does not develop pixel art editing software. Pixelmojo operates Radar, its own self-service AI visibility audit platform, and delivers Vector and Hive as custom builds whose application code the client owns, for B2B companies.',
  slogan: 'AI products you own. Intelligence we maintain.',
  foundingDate: '2024',
  naics: '511210',
  siteName: 'Pixelmojo | AI Product Studio',
  siteDescription: 'AI-native product studio behind Radar, Vector, and Hive',
} as const

export const ADDRESS = {
  '@type': 'PostalAddress' as const,
  streetAddress: '111 Paseo de Roxas, Legazpi Village',
  addressLocality: 'Makati',
  addressRegion: 'Metro Manila',
  postalCode: '1229',
  addressCountry: 'PH',
}

export const GEO = {
  '@type': 'GeoCoordinates' as const,
  latitude: 14.55443,
  longitude: 121.01935,
}

/** Headquarters Place. Distinct from the mailing `address` on the Organization
 *  node: AI engines and Google read foundingLocation/Place as "where the company
 *  is", which a bare PostalAddress does not assert. Counters the recurring
 *  "San Diego, California" hallucination about Pixelmojo's location. */
export const FOUNDING_LOCATION = {
  '@type': 'Place' as const,
  name: 'Makati, Metro Manila, Philippines',
  address: ADDRESS,
  geo: GEO,
}

export const CONTACT_POINT = {
  '@type': 'ContactPoint' as const,
  telephone: '+63-917-165-8601',
  contactType: 'sales',
  email: 'founders@pixelmojo.io',
  availableLanguage: ['English', 'Filipino'],
}

export const AREA_SERVED = [
  { '@type': 'Country' as const, name: 'United States' },
  { '@type': 'Country' as const, name: 'Philippines' },
  { '@type': 'Country' as const, name: 'Singapore' },
  { '@type': 'Country' as const, name: 'Australia' },
  { '@type': 'Country' as const, name: 'United Arab Emirates' },
  { '@type': 'Country' as const, name: 'United Kingdom' },
  { '@type': 'Country' as const, name: 'Canada' },
]

/** Pixelmojo's official YouTube channel. Listed in SAME_AS so the
 *  Organization node names the channel; the channel's own links point back
 *  to pixelmojo.io, which closes the loop in both directions. */
export const YOUTUBE_CHANNEL_URL = 'https://www.youtube.com/@pixelmojohq'

/** Pixelmojo's official profiles. The ONE list: the Organization `sameAs`
 *  and the /press profile links both read it. */
export const OFFICIAL_PROFILES = [
  { label: 'LinkedIn', url: 'https://www.linkedin.com/company/pixelmojo' },
  { label: 'Facebook', url: 'https://www.facebook.com/pixelmojoph' },
  { label: 'YouTube', url: YOUTUBE_CHANNEL_URL },
]

export const SAME_AS = OFFICIAL_PROFILES.map(profile => profile.url)

/** Plain-text knowsAbout topics with no knowledge-graph entity. A topic that
 *  IS an entity (its name or an alternateName) is emitted as a typed @id
 *  reference instead, never repeated here as a string
 *  (entity-identity.test.ts). */
export const KNOWS_ABOUT_TOPICS = [
  'AI Lead Qualification Systems',
  'AI Visibility and Citation Tracking',
  'Answer Engine Optimization',
  'AI Product Architecture',
  'LLM Integration and RAG Systems',
  'Production AI Deployment',
  'Vector Databases',
  'Knowledge Graph Development',
  'Structured Data and Schema Markup',
  'B2B SaaS Product Development',
]

/**
 * Default share-card images.
 *
 * Next merges metadata shallowly: a key a route defines REPLACES the parent's
 * version of that key outright rather than merging into it. So any route that
 * declares its own `openGraph` to set a page-specific title also discards the
 * root layout's `images`, and ships a card with no picture. That is not
 * hypothetical — 30 routes were doing it, including every legal page, and it
 * is invisible on the page that declares it because a share card only renders
 * somewhere else.
 *
 * Spread these into every `openGraph` / `twitter` object a route defines:
 *
 *     openGraph: { title, description, url, images: DEFAULT_OG_IMAGES }
 *     twitter:   { card: 'summary_large_image', title, images: DEFAULT_TWITTER_IMAGES }
 *
 * A route with a genuinely page-specific card supplies its own array instead;
 * the point is that no route should be silent about it.
 */
export const DEFAULT_OG_IMAGE_URL = `${BASE_URL}/pixelmojo-og-2026.png`

export const DEFAULT_OG_IMAGES = [
  {
    url: DEFAULT_OG_IMAGE_URL,
    width: 1200,
    height: 630,
    alt: 'Pixelmojo - AI Product Studio',
  },
]

export const DEFAULT_TWITTER_IMAGES = [DEFAULT_OG_IMAGE_URL]
